import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, tokenStore } from "./api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// status: "loading" | "anon" | "authed" | "offline"
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading");

  const bootstrap = useCallback(async () => {
    if (!tokenStore.get()) {
      setStatus("anon");
      return;
    }
    setStatus("loading");
    try {
      // Validates the saved session and slides its expiry forward, so people stay signed in.
      const { token, user: u } = await api("/auth/me");
      tokenStore.set(token);
      setUser(u);
      setStatus("authed");
    } catch (e) {
      if (e.status === 0) setStatus("offline"); // keep the token, server is just unreachable
      else { tokenStore.clear(); setUser(null); setStatus("anon"); }
    }
  }, []);

  useEffect(() => {
    bootstrap();
    const onExpired = () => { setUser(null); setStatus("anon"); };
    window.addEventListener("lsp-auth-expired", onExpired);
    return () => window.removeEventListener("lsp-auth-expired", onExpired);
  }, [bootstrap]);

  const login = async (username, password) => {
    const { token, user: u } = await api("/auth/login", { method: "POST", body: { username, password } });
    tokenStore.set(token);
    setUser(u);
    setStatus("authed");
  };

  const logout = () => {
    tokenStore.clear();
    setUser(null);
    setStatus("anon");
  };

  const changePassword = async (currentPassword, newPassword) => {
    const { token, user: u } = await api("/auth/change-password", {
      method: "POST",
      body: { currentPassword, newPassword },
    });
    tokenStore.set(token);
    setUser(u);
  };

  const value = useMemo(
    () => ({ user, status, login, logout, changePassword, retry: bootstrap }),
    [user, status, bootstrap] // eslint-disable-line react-hooks/exhaustive-deps
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
